/**
 * A URL in a Sentry payload keeps its path and loses its query and fragment,
 * wherever in the payload it stands. What the SDK attaches moves from one
 * version to the next, so nothing here names a field of the event: every
 * string is read, and a key that holds a query and nothing else goes.
 *
 * The twin of `optivo-service/src/sentryUrlCut.ts`, held to it by
 * `runtimeSentryConfig.parity.test.ts`.
 */

/** A URL, absolute or from the root, split where its query or fragment begins. */
const URL_WITH_QUERY = /(?<![\w.:/-])((?:[a-z][a-z\d+.-]*:\/\/|\/)[^\s?#"'`<>]*)[?#][^\s"'`<>]*/gi;

/** Keys that hold a query or a fragment with no URL around it to be cut from. */
const QUERY_KEYS = new Set(["query_string", "http.query", "http.fragment", "url.query", "url.fragment"]);

/** The SDK's own input, never sent: it holds scopes, which are not data. */
const SDK_INPUT_KEY = "sdkProcessingMetadata";

function isObject(value: unknown): value is Record<string, unknown> {
    return Object.prototype.toString.call(value) === "[object Object]";
}

/** A copy with every URL in it cut. A breadcrumb the SDK still holds stays as it was. */
export function cutUrlQueries<T>(value: T): T {
    if (typeof value === "string") return value.replace(URL_WITH_QUERY, "$1") as T;
    if (Array.isArray(value)) return value.map(cutUrlQueries) as T;
    if (!isObject(value)) return value;
    const cut: Record<string, unknown> = {};
    for (const [key, field] of Object.entries(value)) {
        if (QUERY_KEYS.has(key)) continue;
        cut[key] = key === SDK_INPUT_KEY ? field : cutUrlQueries(field);
    }
    return cut as T;
}
